import React from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import MarkerManager from './MarkerManager';
import { isSameVehicleStatus } from 'src/app/utils/vehicleService';

// react-leaflet so prop bằng reference: icon mới → setIcon (Leaflet gán lại innerHTML), mảng
// position mới → setLatLng, object eventHandlers mới → off/on listener. Các cache dưới giữ nguyên
// reference khi giá trị không đổi, để marker và cụm không đổi không đụng tới DOM.
const CLUSTER_ICON_CACHE_LIMIT = 2000;
const CLUSTER_MARKER_CACHE_LIMIT = 5000;
const clusterIconCache = new Map();
const clusterMarkerCache = new Map();
const vehiclePositionCache = new WeakMap();

const cachedClusterIcon = (key, createIcon) => {
  let icon = clusterIconCache.get(key);
  if (!icon) {
    if (clusterIconCache.size >= CLUSTER_ICON_CACHE_LIMIT) clusterIconCache.clear();
    icon = createIcon();
    clusterIconCache.set(key, icon);
  }
  return icon;
};

const stableClusterMarker = (clusterId, lat, lng, mapRef) => {
  const cached = clusterMarkerCache.get(clusterId);
  if (cached && cached.lat === lat && cached.lng === lng && cached.mapRef === mapRef) return cached;
  if (clusterMarkerCache.size >= CLUSTER_MARKER_CACHE_LIMIT) clusterMarkerCache.clear();
  const position = [lat, lng];
  const entry = {
    lat,
    lng,
    mapRef,
    position,
    eventHandlers: {
      click: () => mapRef.flyTo(position, mapRef.getZoom() + 2, { animate: true })
    }
  };
  clusterMarkerCache.set(clusterId, entry);
  return entry;
};

const stableVehiclePosition = vehicle => {
  let position = vehiclePositionCache.get(vehicle);
  if (!position) {
    position = [vehicle.lat, vehicle.lng];
    vehiclePositionCache.set(vehicle, position);
  }
  return position;
};

const ClusterManager = ({
  clusters,
  mapRef,
  bounds,
  carStatus,
  vehicleSelected,
  clusterCounts,
  vehiclePositionsRef,
  markerRef,
  vehicleIconsRef,
  handleMarkerClick
}) => {
  const visibleClusters = React.useMemo(() => {
    if (!mapRef || !clusters) return [];
    const mapBounds = mapRef.getBounds().pad(0.5);
    return clusters.filter(cluster => {
      const [lng, lat] = cluster.geometry.coordinates;
      return mapBounds.contains([lat, lng]);
    });
  }, [clusters, mapRef, bounds]);

  const displayClusterIcon = (cluster) => {
    const count = clusterCounts.counts[cluster.properties.cluster_id] || 0;
    if (count <= 1) return null;

    let bg = '#1976d2';
    if (count < 10) bg = '#00ABE1CC';
    else if (count <= 50) bg = '#F9CF26CC';
    else bg = '#FF7E55CC';

    const size = count < 100 ? 40 : count < 1000 ? 50 : 60;
    return cachedClusterIcon(`${bg}|${size}|${count}`, () =>
      L.divIcon({
        html: `<div style="background:${bg};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#212529;font-weight:bold;font-size:14px;">${count}</div>`,
        className: 'custom-cluster',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
      })
    );
  };

  return (
    <>
      {visibleClusters.map((cluster) => {
        const [lng, lat] = cluster.geometry.coordinates;
        const { cluster: isCluster, cluster_id, vehicle } = cluster.properties;

        if (isCluster) {
          const count = clusterCounts.counts[cluster_id] || 0;
          if (count === 1) {
            const single = clusterCounts.singleVehicles[cluster_id];
            if (!single) return null;

            const isVisible = !carStatus || isSameVehicleStatus(carStatus, single.device_status);
            const pos = vehiclePositionsRef.current[single.device_id] || stableVehiclePosition(single);
            const inView = mapRef?.getBounds().contains(pos);

            return (
              <MarkerManager
                key={single.device_id}
                vehicle={single}
                position={pos}
                isSelected={false}
                isInViewport={inView}
                onClick={handleMarkerClick}
                markerRef={markerRef}
                vehicleIconsRef={vehicleIconsRef}
                opacity={isVisible ? 1 : 0}
                mapRef={mapRef}
              />
            );
          }

          const icon = displayClusterIcon(cluster);
          if (!icon) return null;
          const clusterMarker = stableClusterMarker(cluster_id, lat, lng, mapRef);

          return (
            <Marker
              key={`cluster-${cluster_id}`}
              position={clusterMarker.position}
              icon={icon}
              eventHandlers={clusterMarker.eventHandlers}
            />
          );
        }

        if (!vehicle || vehicleSelected.device_id === vehicle.device_id) return null;

        const isVisible = !carStatus || isSameVehicleStatus(carStatus, vehicle.device_status);
        const pos = vehiclePositionsRef.current[vehicle.device_id] || stableVehiclePosition(vehicle);
        const inView = mapRef?.getBounds().contains(pos);

        return (
          <MarkerManager
            key={vehicle.device_id}
            vehicle={vehicle}
            position={pos}
            isSelected={false}
            isInViewport={inView}
            onClick={handleMarkerClick}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            opacity={isVisible ? 1 : 0}
            mapRef={mapRef}
          />
        );
      })}
    </>
  );
};

export default React.memo(ClusterManager);
